import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IGetGeneralUsersThunkArgs,
	IUpdateUserThunkArgs,
	IUserOption,
} from 'src/features/user/domain/types';
import {
	IUser,
	ICreateUserThunkArgs,
	IGetUsersThunkArgs,
} from 'src/features/user/domain/types';
import { userApi } from 'src/features/user/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const USER_LOADING_KEY = 'user';

export const getUsers = createAsyncThunk<
	IPaginatedResponse<IUser>,
	IGetUsersThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user/getUsers',
	async ({ request, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: true }));
			const response = await userApi.getUsers(request);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch users');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: false }));
		}
	},
);

export const getGeneralUsers = createAsyncThunk<
	IUserOption[],
	IGetGeneralUsersThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user/getGeneralUsers',
	async ({ onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: true }));
			const response = await userApi.getGeneralUsers();

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch general users',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: false }));
		}
	},
);

export const createUser = createAsyncThunk<
	IUser,
	ICreateUserThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user/createUser',
	async ({ request, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: true }));
			const response = await userApi.createUser(request);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create user');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: false }));
		}
	},
);

export const updateUser = createAsyncThunk<
	IUser,
	IUpdateUserThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user/updateUser',
	async (
		{ request, userId, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: true }));
			const response = await userApi.updateUser(request, userId);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update user');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: USER_LOADING_KEY, loading: false }));
		}
	},
);
